// src/components/admin/AdminDashboard.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../firebase';
import Navbar from '../layout/Navbar';
import AdminNav from './AdminNav';
import StudentUpload from './StudentUpload';

interface UserData {
  uid: string;
  email: string;
  displayName: string;
  password?: string;
  role: 'admin' | 'student';
  createdAt: any;
  lastLogin?: any;
  forms?: string[]; // Add this property to store form IDs
}

interface FormData {
  id: string;
  userId: string;
  studentName?: string;
  lastSaved: any;
  progress: number;
  timestamp: any;
}

const AdminDashboard: React.FC = () => {
  // User management state
  const [users, setUsers] = useState<UserData[]>([]);
  const [searchUserTerm, setSearchUserTerm] = useState('');
  const [userManagementTab, setUserManagementTab] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Forms state
  const [forms, setForms] = useState<FormData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchFormTerm, setSearchFormTerm] = useState('');
  
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // Check if user is admin
  const [isAdmin, setIsAdmin] = useState(false);
  
  useEffect(() => {
    const checkAdmin = async () => {
      if (!currentUser) return;
      
      // Simple check for admin email
      if (currentUser.email === 'jhatcher4@ccc.edu' || currentUser.email === 'admin@example.com') {
        setIsAdmin(true);
        return;
      }
      
      try {
        // Check in users collection
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists() && userDoc.data().role === 'admin') {
          setIsAdmin(true);
        }
      } catch (err) {
        console.error("Error checking admin status:", err);
      }
    };
    
    checkAdmin();
  }, [currentUser]);
  
  // Load users from Firestore
  useEffect(() => {
    if (!isAdmin) return;
    
    const fetchUsers = async () => {
      try {
        // Get users from users collection
        const usersRef = collection(db, 'users');
        const querySnapshot = await getDocs(usersRef);
        
        const usersData: UserData[] = [];
        const userPromises: any[] = [];
        
        // First pass to get basic user info
        querySnapshot.forEach((doc) => {
          const userData = doc.data() as UserData;
          usersData.push({
            ...userData,
            uid: doc.id // Ensure ID is set
          });
          
          // Create a promise to find forms for this user
          const promise = getDocs(
            query(collection(db, 'pcrForms'), where('userId', '==', doc.id))
          ).then(formSnap => {
            // Add form IDs to user object
            const formIds = formSnap.docs.map(d => d.id);
            const userIndex = usersData.findIndex(u => u.uid === doc.id);
            if (userIndex >= 0) {
              usersData[userIndex].forms = formIds;
            }
          });
          
          userPromises.push(promise);
        });
        
        // Wait for all form queries to complete
        await Promise.all(userPromises);
        
        setUsers(usersData);
      } catch (err) {
        console.error("Error fetching users:", err);
        setError("Failed to load users");
      }
    };
    
    fetchUsers();
  }, [isAdmin]);

  // Load forms from Firestore
  useEffect(() => {
    if (!isAdmin) return;
    
    const fetchForms = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get all PCR forms from Firestore
        const formsRef = collection(db, 'pcrForms');
        const querySnapshot = await getDocs(formsRef);
        
        const formsData: FormData[] = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          
          // Calculate progress - this is a simplified version
          const progress = calculateProgress(data);
          
          formsData.push({
            id: doc.id,
            userId: data.userId || 'unknown',
            studentName: data.studentName || '',
            lastSaved: data.lastSaved,
            timestamp: data.timestamp,
            progress
          });
        });
        
        setForms(formsData);
      } catch (err) {
        console.error("Error fetching forms:", err);
        setError("Failed to load form data");
      } finally {
        setLoading(false);
      }
    };
    
    fetchForms();
  }, [isAdmin]);
  
  // Calculate the completion percentage of a form
  const calculateProgress = (formData: any): number => {
    let filledFields = 0;
    let totalFields = 0;
    
    // Count non-empty fields in a form section
    const countFields = (obj: any) => {
      if (!obj || typeof obj !== 'object') return;
      
      Object.entries(obj).forEach(([key, value]) => {
        if (key !== 'userId' && key !== 'timestamp' && key !== 'lastSaved' && key !== 'dispatchScenario' && 
            key !== 'studentName' && key !== 'studentEmail' && key !== 'studentId') {
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            countFields(value);
          } else {
            totalFields++;
            if (value !== '' && value !== null && value !== 0 && 
                !(Array.isArray(value) && value.length === 0)) {
              filledFields++;
            }
          }
        }
      });
    };
    
    countFields(formData);
    return totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;
  };
  
  // Toggle user role between 'admin' and 'student'
  const toggleUserRole = async (userId: string, currentRole: 'admin' | 'student') => {
    try {
      const newRole = currentRole === 'admin' ? 'student' : 'admin';
      
      // Update in Firestore
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        role: newRole
      });
      
      // Update in state
      setUsers(prev => prev.map(user => 
        user.uid === userId ? { ...user, role: newRole } : user
      ));
      
      setSuccessMessage(`User role updated to ${newRole}`);
      setError(null);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      
    } catch (err) {
      console.error("Error updating role:", err);
      setError("Failed to update role: " + (err as Error).message);
    }
  };
  
  // Function to sync student names with forms
const syncStudentNames = async () => {
  try {
    setLoading(true);
    setError(null);
    
    let updatedCount = 0;
    const updatePromises = [];
    
    // Check each form
    for (const form of forms) {
      // If form has no student name but has a userId
      if (form.userId && (!form.studentName || form.studentName === '')) {
        // Find matching user
        const matchingUser = users.find(user => user.uid === form.userId);
        
        if (matchingUser && matchingUser.displayName) {
          // Update form with user's displayName
          const formRef = doc(db, 'pcrForms', form.id);
          updatePromises.push(
            updateDoc(formRef, {
              studentName: matchingUser.displayName
            })
          );
          updatedCount++;
        }
      }
    }
    
    // Wait for all updates to complete
    await Promise.all(updatePromises);
    
    if (updatedCount > 0) {
      setSuccessMessage(`Updated student names for ${updatedCount} forms`);
      // Refresh the forms data
      await refreshData();
    } else {
      setSuccessMessage("No forms needed name updates");
    }
    
    setTimeout(() => setSuccessMessage(null), 3000);
  } catch (err) {
    console.error("Error syncing names:", err);
    setError("Failed to sync names: " + (err as Error).message);
  } finally {
    setLoading(false);
  }
};
  // Export users list as CSV
  // Export users list as CSV with Form IDs
const exportUsersCsv = () => {
  try {
    // Create a clear mapping between user IDs and their forms
    const userFormMap: { [userId: string]: { formId: string, studentName: string }[] } = {};
    
    // Process the forms array to build the mapping
    forms.forEach(form => {
      if (form.userId && form.id) {
        if (!userFormMap[form.userId]) {
          userFormMap[form.userId] = [];
        }
        
        // Store both the form ID and student name
        userFormMap[form.userId].push({
          formId: form.id,
          studentName: form.studentName || 'No Name'
        });
      }
    });
    
    // Debug - check what forms are mapped to users
    console.log("User-Form Mapping:", userFormMap);
    
    // Create CSV content
    const csvContent = [
      // Header row
      ["UID", "Email", "Name", "Password", "Role", "Form IDs", "Student Names", "Form Count"].join(","),
      // Data rows
      ...users.map(user => {
        const userForms = userFormMap[user.uid] || [];
        const formIds = userForms.map(f => f.formId).join("; ");
        const formNames = userForms.map(f => f.studentName).join("; ");
        
        return [
          user.uid,
          `"${user.email}"`, // Quote email to handle commas
          `"${user.displayName || 'No Name'}"`, // Quote names to handle commas
          user.password || "N/A",
          user.role,
          formIds || "No Forms",
          `"${formNames || 'No Names'}"`, // Quote names to handle commas
          userForms.length // Add a count for quick reference
        ].join(",");
      })
    ].join("\n");
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `users_forms_${new Date().toISOString()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    setSuccessMessage("User data exported successfully with form information");
    setTimeout(() => setSuccessMessage(null), 3000);
  } catch (err) {
    console.error("Error exporting CSV:", err);
    setError("Failed to export user data: " + (err as Error).message);
  }
};
  
  const createFormForNewUser = async () => {
    if (!currentUser) return;
    
    try {
      // Generate a random user ID (in a real app, this would be a Firebase Auth UID)
      const randomUserId = 'user_' + Math.random().toString(36).substr(2, 9);
      
      // Create new form
      const formTemplate = {
        userId: randomUserId,
        timestamp: serverTimestamp(),
        lastSaved: serverTimestamp(),
        dispatchScenario: `At 19:41 you are dispatched to the scene of a winery in the River North neighborhood for "Altered Mental Status". Your patient is a 48 y/o female who is found sitting in a chair being tended to by the bar staff. The first thing you notice is that she is morbidly obese. As you approach, a man who states that he "called 911" and that this is "only their second date". The man then explains that they had just arrived at the restaurant and ordered a glass of wine at the standing bar (no food), while waiting to be seated. He tells you that (prior to your arrival) he had excused himself to the restroom, and when he returned, he found his date was standing there and her glass of wine was shattered on the floor. "All of a sudden she became disoriented."
You begin your assessment and she is able to tell you her name and location, but she is aphasic. You also notice that your PT has an insulin pump clipped to her belt, and your partner finds Metoprolol in her purse. When asked, the PT complains of "dizziness" and "nausea". No medic alert bracelet found. Initial vital signs are: BP 160/110, HR 134, RR 24 (shallow), skin is cool, pale, and diaphoretic, both pupils appear to have a gaze.`
      };
      
      const docRef = await addDoc(collection(db, 'pcrForms'), formTemplate);
      
      // Add to forms list
      setForms(prev => [...prev, {
        id: docRef.id,
        userId: randomUserId,
        lastSaved: new Date(),
        timestamp: new Date(),
        progress: 0,
        studentName: ''
      }]);
      
      setSuccessMessage(`Successfully created form with ID: ${docRef.id}. This form is assigned to a generated user ID: ${randomUserId}`);
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      
    } catch (err) {
      console.error("Error creating form:", err);
      setError("Failed to create form: " + (err as Error).message);
    }
  };
  
  const handleViewForm = (formId: string) => {
    navigate(`/form/${formId}`);
  };
  
  // Refresh user and form lists
  const refreshData = async () => {
    try {
      setLoading(true);
      
      // Fetch users
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      
      const usersData: UserData[] = [];
      usersSnapshot.forEach((doc) => {
        usersData.push(doc.data() as UserData);
      });
      
      setUsers(usersData);
      
      // Fetch forms
      const formsRef = collection(db, 'pcrForms');
      const formsSnapshot = await getDocs(formsRef);
      
      const formsData: FormData[] = [];
      
      formsSnapshot.forEach((doc) => {
        const data = doc.data();
        
        // Calculate progress
        const progress = calculateProgress(data);
        
        formsData.push({
          id: doc.id,
          userId: data.userId || 'unknown',
          studentName: data.studentName || '',
          lastSaved: data.lastSaved,
          timestamp: data.timestamp,
          progress
        });
      });
      
      setForms(formsData);
      setSuccessMessage("Data refreshed successfully");
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      
    } catch (err) {
      console.error("Error refreshing data:", err);
      setError("Failed to refresh data");
    } finally {
      setLoading(false);
    }
  };
  
  // Filter forms based on search term
  const filteredForms = forms.filter(form => 
    form.userId.toLowerCase().includes(searchFormTerm.toLowerCase()) ||
    form.id.toLowerCase().includes(searchFormTerm.toLowerCase()) ||
    (form.studentName && form.studentName.toLowerCase().includes(searchFormTerm.toLowerCase()))
  );
  
  // Filter users based on search term
  const filteredUsers = users.filter(user => 
    user.email.toLowerCase().includes(searchUserTerm.toLowerCase()) ||
    (user.displayName && user.displayName.toLowerCase().includes(searchUserTerm.toLowerCase())) ||
    user.uid.toLowerCase().includes(searchUserTerm.toLowerCase())
  );
  
  // Format date for display
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Never';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString();
    } catch (e) {
      return 'Invalid date';
    }
  };
  
  // Check for admin access
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Navbar />
        <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6">
              <h1 className="text-xl font-bold text-red-600">Access Denied</h1>
              <p className="mt-2">Only administrators can access this page.</p>
            </div>
          </div>
        </main>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      <AdminNav />
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          <h1 className="text-2xl font-semibold text-gray-900">Admin Dashboard</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage PCR forms and users from a central location.
          </p>
        </div>
        
        {error && (
          <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <span className="block sm:inline">{error}</span>
            <button 
              onClick={() => setError(null)} 
              className="float-right font-bold"
            >
              ×
            </button>
          </div>
        )}
        
        {successMessage && (
          <div className="mt-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
            <span className="block sm:inline">{successMessage}</span>
          </div>
        )}
        
        {/* Tabs for Forms & Users */}
        <div className="mt-6 border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setUserManagementTab(false)}
              className={`${
                !userManagementTab 
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm`}
            >
              PCR Forms
            </button>
            <button
              onClick={() => setUserManagementTab(true)}
              className={`${
                userManagementTab 
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm`}
            >
              User Management
            </button>
          </nav>
        </div>
        
        {/* Student Upload Component - always visible */}
        <div className="mt-6">
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h2 className="text-lg font-medium text-gray-900">Student Name Mapping</h2>
              <StudentUpload />
            </div>
          </div>
        </div>
        
        {/* Conditional Content Based on Tab Selection */}
        {!userManagementTab ? (
          // PCR Forms Management Tab
          <>
            <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h2 className="text-lg font-medium text-gray-900">Form Actions</h2>
                <div className="mt-2 max-w-xl text-sm text-gray-500">
                  <p>Create new forms or manage existing ones.</p>
                </div>
                <button
                    onClick={syncStudentNames}
                    disabled={loading}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none ml-2"
                  >
                    Sync Missing Names
                  </button>
                <div className="mt-5">
                  <button
                    type="button"
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                    onClick={createFormForNewUser}
                  >
                    Create Form for New User
                  </button>
                  <div className="mt-3 text-sm text-gray-500">
                    <p>This will create a form with a generated user ID.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-medium text-gray-900">All Forms</h2>
                  <div className="flex space-x-2">
                    <div className="relative rounded-md shadow-sm w-64">
                      <input
                        type="text"
                        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pr-10 sm:text-sm border-gray-300 rounded-md"
                        placeholder="Search forms..."
                        value={searchFormTerm}
                        onChange={(e) => setSearchFormTerm(e.target.value)}
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    <button
                      onClick={refreshData}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
                    >
                      Refresh
                    </button>
                  </div>
                </div>
                
                {loading ? (
                  <div className="py-10 flex justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
                  </div>
                ) : (
                  <div className="mt-4 flex flex-col">
                    <div className="tracking-tighter -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                      <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                        <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-tighter">
                                  Form ID
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-tighter">
                                  User ID
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-tighter">
                                  Student Name
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-tighter">
                                  Progress
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-tighter">
                                  Last Activity
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-tighter">
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {filteredForms.length === 0 ? (
                                <tr>
                                  <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                                    No forms found
                                  </td>
                                </tr>
                              ) : (
                                filteredForms.map(form => (
                                  <tr key={form.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                      {form.id}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {form.userId}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {form.studentName || '—'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                                        <div 
                                          className={`h-2.5 rounded-full ${
                                            form.progress > 70 ? 'bg-green-600' : 
                                            form.progress > 30 ? 'bg-yellow-400' : 'bg-red-500'
                                          }`}
                                          style={{ width: `${form.progress}%` }}
                                        ></div>
                                      </div>
                                      <span className="text-xs text-gray-500 ml-2">{form.progress}%</span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {formatDate(form.lastSaved)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                      <button
                                        onClick={() => handleViewForm(form.id)}
                                        className="text-indigo-600 hover:text-indigo-900 mr-3"
                                      >
                                        View Form
                                      </button>
                                      <button
                                        onClick={() => {
                                          const url = `${window.location.origin}/form/${form.id}/readonly`;
                                          navigator.clipboard.writeText(url)
                                            .then(() => alert('Read-only link copied to clipboard!'))
                                            .catch(err => console.error('Failed to copy link: ', err));
                                        }}
                                        className="text-sm flex flex-wrap text-green-600 hover:text-green-900"
                                      >
                                        Copy Read-Only Link
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          // User Management Tab
          <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900">User List</h2>
                <div className="flex space-x-2">
                  <div className="relative rounded-md shadow-sm">
                    <input
                      type="text"
                      className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pr-10 sm:text-sm border-gray-300 rounded-md"
                      placeholder="Search users..."
                      value={searchUserTerm}
                      onChange={(e) => setSearchUserTerm(e.target.value)}
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <button
                    onClick={refreshData}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
                  >
                    Refresh
                  </button>
                  <button
                    onClick={exportUsersCsv}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    Export CSV
                  </button>
                </div>
              </div>
              
              {loading ? (
                <div className="py-10 flex justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
                </div>
              ) : (
                <div className="mt-4 flex flex-col">
                  <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                      <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-tighter">
                                UID
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-tighter">
                                Role
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-tighter">
                                Password
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-tighter">
                                Actions
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-tighter">
                                Forms
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {filteredUsers.length === 0 ? (
                              <tr>
                                <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                                  No users found
                                </td>
                              </tr>
                            ) : (
                              filteredUsers.map(user => (
                                <tr key={user.uid}>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                      <div>
                                        <div className="text-sm font-medium text-gray-900">
                                          {user.displayName || user.email.split('@')[0]}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                          {user.email}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <span className="font-mono text-xs">{user.uid}</span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                      user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'
                                    }`}>
                                      {user.role}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {user.password || 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <button
                                      onClick={() => toggleUserRole(user.uid, user.role)}
                                      className={user.role === 'admin' ? 'text-red-600 hover:text-red-900' : 'text-indigo-600 hover:text-indigo-900'}
                                    >
                                      {user.role === 'admin' ? 'Make Student' : 'Make Admin'}
                                    </button>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    {user.forms && user.forms.length > 0 ? (
                                      <div className="relative">
                                        <select
                                          className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                          onChange={(e) => handleViewForm(e.target.value)}
                                          defaultValue=""
                                        >
                                          <option value="" disabled>Select a form</option>
                                          {user.forms.map(formId => (
                                            <option key={formId} value={formId}>
                                              Form {formId.substring(0, 8)}...
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    ) : (
                                      <span className="text-gray-400">No forms</span>
                                    )}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;